import {type CalendarEntry, CalendarEntryTimeBoundary} from "./calendarEntry";
import {Dictionary} from "../../../page/elements/dictionary";
import {LocatedDates} from "../../../common/utils/dates/locatedDates";
import {resolve} from "../../../container";
import {isSameDay, isSameYear} from "../../../common/utils/dates/dates";
import type {Nullable} from "../../../bootstrap/common/types";


export function renderDateTime(entry: CalendarEntry, dictionary: Dictionary): RenderedDateTime {
    return new RenderDateTime(entry, dictionary).getResult();
}

export type RenderedDateTime = {
    date: string;
    time: Nullable<string>;
}

export class RenderDateTime {
    private result: RenderedDateTime;

    private start: CalendarEntryTimeBoundary;
    private ending: CalendarEntryTimeBoundary;
    private showStartTime: boolean;
    private showEndingTime: boolean;

    public constructor(
        entry: CalendarEntry,
        private dictionary: Dictionary,
        private dates: LocatedDates = resolve(LocatedDates)
    ) {
        this.start = entry.start();
        this.ending = entry.ending();
        this.showStartTime = this.start.showTime;
        this.showEndingTime = this.ending.showTime;

        const fragments = this.renderFragments();
        this.result = {date: this.composeDate(fragments), time: this.composeTime(fragments)};
    }

    public getResult(): RenderedDateTime {
        return this.result;
    }

    private renderFragments(): RenderedFragments {
        return {
            startDate: this.renderStartDate(),
            startTime: this.renderStartTime(),
            endingDate: this.renderEndingDate(),
            endingTime: this.renderEndingTime()
        };
    }

    private renderStartDate(): string {
        const mayOmitYear = !isSameDay(this.start.raw, this.ending.raw) && isSameYear(this.start.raw, this.ending.raw);
        return mayOmitYear
            ? this.renderDateWithoutYear(this.start)
            : this.renderDateWithYear(this.start);
    }

    private renderEndingDate(): string {
        return this.renderDateWithYear(this.ending);
    }

    private renderDateWithYear(item: CalendarEntryTimeBoundary): string {
        return this.dates.toLocalDateString(item.raw);
    }

    private renderDateWithoutYear(item: CalendarEntryTimeBoundary): string {
        return this.dates.toLocalDayMonthDateString(item.raw);
    }

    private renderStartTime(): string {
        const startTimeWithoutWeekday = this.showEndingTime && this.start.isDST === this.ending.isDST
            ? this.renderTime(this.start)
            : this.renderTimeWithZone(this.start);

        return this.addWeekdayIfNeeded(startTimeWithoutWeekday, this.start);
    }

    private renderEndingTime(): string {
        const endingTimeWithoutWeekday = this.renderTimeWithZone(this.ending);
        return this.addWeekdayIfNeeded(endingTimeWithoutWeekday, this.ending);
    }

    private renderTimeWithZone(item: CalendarEntryTimeBoundary): string {
        const withoutZone = this.renderTime(item);
        const key = item.isDST ? "DST_SUMMER" : "DST_WINTER";
        return this.dictionary.translate(key, undefined, {"[TIME]": withoutZone});
    }

    private renderTime(item: CalendarEntryTimeBoundary): string {
        return this.dates.toLocalTimeString(item.raw);
    }

    private addWeekdayIfNeeded(timeWithoutWeekday: string, item: CalendarEntryTimeBoundary): string {
        const showWeekday = !isSameDay(this.start.raw, this.ending.raw);
        const weekdayPrefix = showWeekday
            ? this.dates.toLocalWeekdayString(item.raw, "short") + " "
            : "";
        return weekdayPrefix + timeWithoutWeekday;
    }

    private composeDate(fragments: RenderedFragments): string {
        if (isSameDay(this.start.raw, this.ending.raw)) {
            return this.dictionary.translate("TIME", "DATE", {
                "[DATE]": fragments.startDate
            });
        } else {
            return this.dictionary.translate("TIME", "DATEINTERVAL", {
                "[STARTDATE]": fragments.startDate,
                "[ENDINGDATE]": fragments.endingDate
            });
        }
    }

    private composeTime(fragments: RenderedFragments): Nullable<string> {
        if (this.showStartTime && this.showEndingTime) {
            return this.dictionary.translate("TIME", "TIMEINTERVAL", {
                "[STARTTIME]": fragments.startTime,
                "[ENDINGTIME]": fragments.endingTime
            });
        } else if (this.showEndingTime) {
            return this.dictionary.translate("TIME", "ENDINGTIME", {
                "[ENDINGTIME]": fragments.endingTime
            });
        } else if (this.showStartTime) {
            return this.dictionary.translate("TIME", "STARTTIME", {
                "[STARTTIME]": fragments.startTime
            });
        } else {
            return null;
        }
    }
}

type RenderedFragments = {
    startDate: string;
    startTime: string;
    endingDate: string;
    endingTime: string;
}